import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.reduce.js";
import "core-js/modules/es.array.sort.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { requestIframeClient, requestIframeServer } from 'request-iframe';

/**
 * React hook for using request-iframe client
 * 
 * @param targetFnOrRef - function that returns iframe element or Window object, or a React ref object
 * @param options - client options
 * @param deps - dependency array (optional, for re-creating client when dependencies change)
 * @returns client instance
 * 
 * @example
 * ```tsx
 * // Using function
 * const MyComponent = () => {
 *   const iframeRef = useRef<HTMLIFrameElement>(null);
 *   const client = useClient(() => iframeRef.current, { secretKey: 'my-app' });
 * 
 *   const handleClick = async () => {
 *     if (client) {
 *       const response = await client.send('/api/data', { id: 1 });
 *       console.log(response.data);
 *     }
 *   };
 * 
 *   return (
 *     <div>
 *       <iframe ref={iframeRef} src="/iframe.html" />
 *       <button onClick={handleClick}>Send Request</button>
 *     </div>
 *   );
 * };
 * 
 * // Using ref directly
 * const MyComponent2 = () => {
 *   const iframeRef = useRef<HTMLIFrameElement>(null);
 *   const client = useClient(iframeRef, { secretKey: 'my-app' });
 *   // ...
 * };
 * ```
 */
export function useClient(targetFnOrRef, options, deps) {
  var clientRef = useRef(null);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    client = _useState2[0],
    setClient = _useState2[1];
  var lastTargetRef = useRef(null);
  var targetFnOrRefRef = useRef(targetFnOrRef);
  var optionsRef = useRef(options);

  /** Keep latest inputs without re-creating effect deps */
  targetFnOrRefRef.current = targetFnOrRef;
  optionsRef.current = options;
  var getTarget = useCallback(() => {
    return typeof targetFnOrRefRef.current === 'function' ? targetFnOrRefRef.current() : targetFnOrRefRef.current.current;
  }, []);

  /**
   * Snapshot the current target during render (pure read).
   * We use this value as an effect dependency so the effect only runs when
   * the target actually changes (avoids StrictMode update-depth loops).
   */
  var target = getTarget();
  var destroy = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.destroy();
      clientRef.current = null;
    }
    lastTargetRef.current = null;
  }, []);

  /**
   * Create/destroy client in effect to be compatible with React 18 StrictMode
   * and concurrent rendering (avoid render-phase side effects).
   */
  useEffect(() => {
    /** If target unchanged, keep current client */
    if (target === lastTargetRef.current) return;

    /** Target changed: destroy old client and maybe create a new one */
    if (clientRef.current) {
      clientRef.current.destroy();
      clientRef.current = null;
    }
    lastTargetRef.current = target;
    if (!target) {
      setClient(null);
      return;
    }
    var newClient = requestIframeClient(target, optionsRef.current);
    clientRef.current = newClient;
    setClient(newClient);
    return () => {
      /** Cleanup only if it's still the current client */
      if (clientRef.current === newClient) {
        newClient.destroy();
        clientRef.current = null;
        lastTargetRef.current = null;
      }
    };
  }, deps ? [...deps, target] : [target]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      destroy();
    };
  }, []);
  return client;
}

/**
 * React hook for using request-iframe server
 * 
 * @param options - server options
 * @param deps - dependency array (optional, for re-creating server when dependencies change)
 * @returns server instance
 * 
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const server = useServer({ secretKey: 'my-app' });
 * 
 *   useEffect(() => {
 *     const off = server.on('/api/data', (req, res) => {
 *       res.send({ data: 'Hello' });
 *     });
 *     return off;
 *   }, [server]);
 * 
 *   return <div>Server Component</div>;
 * };
 * ```
 */
export function useServer(options, deps) {
  var serverRef = useRef(null);
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    server = _useState4[0],
    setServer = _useState4[1];
  var optionsRef = useRef(options);
  optionsRef.current = options;
  var destroy = useCallback(() => {
    if (serverRef.current) {
      serverRef.current.destroy();
      serverRef.current = null;
    }
  }, []);

  /**
   * Create/destroy server in effect to be compatible with React 18 StrictMode
   * and concurrent rendering (avoid render-phase side effects).
   */
  useEffect(() => {
    if (serverRef.current) {
      serverRef.current.destroy();
      serverRef.current = null;
    }
    var newServer = requestIframeServer(optionsRef.current);
    serverRef.current = newServer;
    setServer(newServer);
    return () => {
      if (serverRef.current === newServer) {
        newServer.destroy();
        serverRef.current = null;
      }
    };
  }, deps !== null && deps !== void 0 ? deps : []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      destroy();
    };
  }, []);
  return server;
}

/**
 * React hook for registering server handlers
 * 
 * @param server - server instance (from useServer)
 * @param path - route path
 * @param handler - handler function
 * @param deps - dependency array (optional, for re-registering when dependencies change)
 * 
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const server = useServer();
 *   const [userId, setUserId] = useState(1);
 * 
 *   // Register handler that depends on userId
 *   useServerHandler(server, '/api/user', (req, res) => {
 *     res.send({ userId, data: 'Hello' });
 *   }, [userId]);
 * 
 *   return <div>Server Component</div>;
 * };
 * ```
 */
export function useServerHandler(server, path, handler, deps) {
  var handlerRef = useRef(handler);
  handlerRef.current = handler;
  var handlerWrapper = useCallback((req, res) => {
    var _handlerRef$current;
    return (_handlerRef$current = handlerRef.current) === null || _handlerRef$current === void 0 ? void 0 : _handlerRef$current.call(handlerRef, req, res);
  }, []);
  useEffect(() => {
    if (!server) {
      return;
    }

    // Register handler with stable wrapper
    var off = server.on(path, handlerWrapper);

    // Cleanup: unregister handler on unmount or when deps change
    return off;
  }, [server, path, handlerWrapper, ...(deps || [])]);
}

/**
 * React hook for registering server handlers map
 * 
 * @param server - server instance (from useServer)
 * @param map - map of route paths and handler functions
 * @param deps - dependency array (optional, for re-registering when dependencies change)
 * 
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const server = useServer();
 *   const [userId, setUserId] = useState(1);
 * 
 *   // Register handlers using map
 *   useServerHandlerMap(server, {
 *     '/api/user': (req, res) => {
 *       res.send({ userId, data: 'Hello' });
 *     },
 *     '/api/user2': (req, res) => {
 *       res.send({ userId, data: 'Hello' });
 *     }
 *   }, [userId]);
 * 
 *   return <div>Server Component</div>;
 * };
 * ```
 */
export function useServerHandlerMap(server, map, deps) {
  var mapRef = useRef(map);
  mapRef.current = map;
  var keys = useMemo(() => {
    return Object.keys(map).sort();
  }, [map]);
  var keysStr = useMemo(() => {
    return keys.join(',');
  }, [keys]);
  var mapWrapper = useMemo(() => {
    return keys.reduce((acc, key) => {
      acc[key] = function (req, res) {
        var _mapRef$current;
        return (_mapRef$current = mapRef.current) === null || _mapRef$current === void 0 || (_mapRef$current = _mapRef$current[key]) === null || _mapRef$current === void 0 ? void 0 : _mapRef$current.call(this, req, res);
      };
      return acc;
    }, {});
  }, [keysStr]);
  useEffect(() => {
    if (!server) {
      return;
    }
    // Register handlers using map with stable wrappers
    var off = server.map(mapWrapper);

    // Cleanup: unregister all handlers on unmount or when deps change
    return off;
  }, [server, mapWrapper, ...(deps || [])]);
}